import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getBookingWidget, saveBookingWidget, getServices } from './businessApi';

const PUBLIC_URL = (businessId) => `https://app.senselifting.com/book/${businessId}`;

const IFRAME_CODE = (businessId) =>
  `<iframe src="${PUBLIC_URL(businessId)}" width="100%" height="700" frameborder="0" style="border-radius:16px"></iframe>`;

const SCRIPT_CODE = (businessId) =>
  `<script src="https://app.senselifting.com/widget.js" data-biz="${businessId}" data-color="${'#00B4D8'}" async></script>`;


function CopyButton({ text, label, nl }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy}
      style={{ background: copied ? '#4AC87A20' : `${_T.accent}18`, border: `1px solid ${copied ? '#4AC87A55' : `${_T.accent}44`}`, borderRadius: 10, color: copied ? '#4AC87A' : _T.accent, fontSize: 12, fontWeight: 600, padding: '8px 14px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
      {copied ? `✓ ${nl ? 'Gekopieerd' : 'Copied'}` : (label || (nl ? 'Kopiëren' : 'Copy'))}
    </button>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)}
      style={{ width: 44, height: 24, borderRadius: 12, background: value ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: `1px solid ${value ? _T.accent : _T.cardBorderBot}`, cursor: 'pointer', position: 'relative', padding: 0, flexShrink: 0, transition: 'all 0.2s' }}>
      <div style={{ position: 'absolute', top: 4, left: value ? 22 : 4, width: 16, height: 16, borderRadius: '50%', background: value ? '#0a1220' : C.silver, transition: 'left 0.2s' }} />
    </button>
  );
}

function CodeBox({ code }) {
  return (
    <pre style={{ background: '#0a1220', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, padding: 14, overflowX: 'auto', color: '#a8d8ea', fontSize: 12, margin: 0, lineHeight: 1.7, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
      {code}
    </pre>
  );
}

const EMPTY_SETTINGS = {
  enabled: true,
  service_ids: [],
  accent_color: '#00B4D8',
  business_name: '',
  welcome_nl: '',
  welcome_en: '',
};

export default function BusinessBookingWidget({ lang, businessId, role }) {
  const nl      = lang === 'nl';
  const canEdit = role === 'owner' || role === 'admin';

  const [tab, setTab]         = useState('setup');
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getBookingWidget(businessId).catch(() => null),
      getServices(businessId).catch(() => []),
    ]).then(([widget, svcs]) => {
      if (widget && !widget.error) {
        setSettings({ ...EMPTY_SETTINGS, ...widget });
        // api_key handled by BusinessApiScreen
      }
      setServices(Array.isArray(svcs) ? svcs : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    await saveBookingWidget(businessId, settings).catch(() => {});
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const toggleService = (sid) => {
    setSettings(s => ({
      ...s,
      service_ids: s.service_ids.includes(sid)
        ? s.service_ids.filter(x => x !== sid)
        : [...s.service_ids, sid],
    }));
  };

  const tabs = [
    { id: 'setup',    label: nl ? 'Instellingen' : 'Setup' },
    { id: 'embed',    label: nl ? 'Insluiten' : 'Embed' },
    { id: 'link',     label: nl ? 'Boekingslink' : 'Booking Link' },
  ];

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: C.silver, fontSize: 13 }}>...</div>;

  return (
    <div style={{ paddingBottom: 40 }}>
      <style>{CSS}</style>

      {/* Tab strip */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flexShrink: 0, padding: '9px 16px', borderRadius: 10, border: `1.5px solid ${tab === t.id ? _T.accent : _T.cardBorderBot}`, background: tab === t.id ? `${_T.accent}18` : _T.bg3, color: tab === t.id ? _T.accent : C.silver, fontSize: 12, fontWeight: tab === t.id ? 700 : 400, cursor: 'pointer' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab 1: Widget Setup ── */}
      {tab === 'setup' && (
        <div>
          {/* Enable toggle */}
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{nl ? 'Online boekingen inschakelen' : 'Enable online bookings'}</div>
                <div style={{ color: C.silver2, fontSize: 12, marginTop: 3 }}>
                  {nl ? 'Klanten kunnen online boeken via de publieke pagina.' : 'Customers can book online via the public page.'}
                </div>
              </div>
              <Toggle value={settings.enabled} onChange={v => setSettings(s => ({ ...s, enabled: v }))} />
            </div>
          </div>

          {/* Business name */}
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{nl ? 'Bedrijfsnaam in widget' : 'Business name in widget'}</div>
            <input value={settings.business_name} onChange={e => setSettings(s => ({ ...s, business_name: e.target.value }))}
              placeholder={nl ? 'Naam zoals getoond in de widget' : 'Name shown in the widget'}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none' }} />
          </div>

          {/* Welcome messages */}
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{nl ? 'Welkomstbericht' : 'Welcome message'}</div>
            <label style={{ color: C.silver, fontSize: 11, display: 'block', marginBottom: 8 }}>
              🇳🇱 Nederlands
              <textarea value={settings.welcome_nl} onChange={e => setSettings(s => ({ ...s, welcome_nl: e.target.value }))}
                rows={2} placeholder="Welkom! Boek eenvoudig een afspraak."
                style={{ display: 'block', marginTop: 4, width: '100%', boxSizing: 'border-box', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none', resize: 'vertical' }} />
            </label>
            <label style={{ color: C.silver, fontSize: 11, display: 'block' }}>
              🇬🇧 English
              <textarea value={settings.welcome_en} onChange={e => setSettings(s => ({ ...s, welcome_en: e.target.value }))}
                rows={2} placeholder="Welcome! Book your session easily."
                style={{ display: 'block', marginTop: 4, width: '100%', boxSizing: 'border-box', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none', resize: 'vertical' }} />
            </label>
          </div>

          {/* Accent color */}
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{nl ? 'Accentkleur' : 'Accent color'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="color" value={settings.accent_color}
                onChange={e => setSettings(s => ({ ...s, accent_color: e.target.value }))}
                style={{ width: 44, height: 44, borderRadius: 10, border: `1px solid ${_T.cardBorderBot}`, padding: 2, background: 'transparent', cursor: 'pointer' }} />
              <input value={settings.accent_color}
                onChange={e => setSettings(s => ({ ...s, accent_color: e.target.value }))}
                style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none' }} />
            </div>
          </div>

          {/* Services */}
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{nl ? 'Zichtbare services in widget' : 'Services shown in widget'}</div>
            {services.length === 0 ? (
              <div style={{ color: C.silver2, fontSize: 12 }}>{nl ? 'Geen services gevonden' : 'No services found'}</div>
            ) : (
              services.map(s => {
                const isOn = settings.service_ids.length === 0 || settings.service_ids.includes(s.id);
                return (
                  <div key={s.id} onClick={() => toggleService(s.id)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${_T.cardBorderBot}`, cursor: 'pointer' }}>
                    <div>
                      <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                      <div style={{ color: C.silver2, fontSize: 11 }}>{s.duration_min}min {s.price != null ? `· €${parseFloat(s.price).toFixed(2)}` : ''}</div>
                    </div>
                    <div style={{ width: 20, height: 20, borderRadius: 5, background: isOn ? `${_T.accent}25` : _T.bg4, border: `1.5px solid ${isOn ? _T.accent : _T.cardBorderBot}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: _T.accent, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                      {isOn ? '✓' : ''}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Sign-up notice */}
          <div style={{ background: `${_T.accent}10`, border: `1px solid ${_T.accent}33`, borderRadius: 14, padding: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 18 }}>🔒</span>
              <div>
                <div style={{ color: _T.accent, fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{nl ? 'Account vereist' : 'Account required'}</div>
                <div style={{ color: C.silver, fontSize: 12 }}>
                  {nl
                    ? 'Klanten moeten een Sense Lifting account aanmaken om te boeken. Dit is altijd ingeschakeld.'
                    : 'Customers must create a Sense Lifting account to book. This is always on.'}
                </div>
              </div>
            </div>
          </div>

          {/* Save */}
          {canEdit && (
            <button onClick={handleSave} disabled={saving}
              style={{ width: '100%', background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 14, color: '#0a1220', fontSize: 14, fontWeight: 700, padding: '14px', cursor: 'pointer' }}>
              {saving ? '...' : saved ? `✓ ${nl ? 'Opgeslagen' : 'Saved'}` : (nl ? 'Instellingen opslaan' : 'Save widget settings')}
            </button>
          )}
        </div>
      )}

      {/* ── Tab 2: Embed Code ── */}
      {tab === 'embed' && (
        <div>
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>iFrame {nl ? 'insluiten' : 'embed'}</div>
              <CopyButton text={IFRAME_CODE(businessId)} nl={nl} />
            </div>
            <CodeBox code={IFRAME_CODE(businessId)} />
          </div>

          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>Script {nl ? 'insluiten' : 'embed'}</div>
              <CopyButton text={SCRIPT_CODE(businessId)} nl={nl} />
            </div>
            <CodeBox code={SCRIPT_CODE(businessId)} />
          </div>

          <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{nl ? 'Instructies' : 'Instructions'}</div>
            <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.8 }}>
              {nl
                ? '1. Kopieer de iFrame of script code.\n2. Plak deze in de HTML van je website, op de plek waar je de widget wilt tonen.\n3. De widget past zich automatisch aan aan de breedte van de container.\n4. Klanten kunnen direct boeken via jouw website.'
                : '1. Copy the iFrame or script code.\n2. Paste it into your website HTML where you want the widget to appear.\n3. The widget automatically adapts to the container width.\n4. Customers can book directly from your website.'}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 3: Booking Form Link ── */}
      {tab === 'link' && (
        <div>
          {/* Public URL */}
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{nl ? 'Publieke boekingslink' : 'Public booking link'}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, padding: '10px 14px', color: _T.accent, fontSize: 12, fontWeight: 600, wordBreak: 'break-all' }}>
                {PUBLIC_URL(businessId)}
              </div>
              <CopyButton text={PUBLIC_URL(businessId)} nl={nl} />
            </div>
          </div>

          {/* QR code */}
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>QR Code</div>
            <div style={{ background: _T.bg4, border: `2px dashed ${_T.cardBorderBot}`, borderRadius: 12, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📱</div>
              <div style={{ color: C.silver, fontSize: 12 }}>{nl ? 'QR code beschikbaar in de volledige versie' : 'QR code available in the full version'}</div>
              <div style={{ color: C.silver2, fontSize: 11, marginTop: 4 }}>{PUBLIC_URL(businessId)}</div>
            </div>
          </div>

          {/* Email template */}
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>{nl ? 'E-mail template' : 'Email template'}</div>
              <CopyButton text={nl
                ? `Beste klant,\n\nWe nodigen je uit om eenvoudig een afspraak te boeken via onze online boekingspagina:\n\n${PUBLIC_URL(businessId)}\n\nMet vriendelijke groet,\n${settings.business_name || 'Het team'}`
                : `Dear customer,\n\nWe invite you to easily book an appointment via our online booking page:\n\n${PUBLIC_URL(businessId)}\n\nKind regards,\n${settings.business_name || 'The team'}`
              } nl={nl} />
            </div>
            <div style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, padding: 14, color: C.silver, fontSize: 12, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
              {nl
                ? `Beste klant,\n\nWe nodigen je uit om eenvoudig een afspraak te boeken via onze online boekingspagina:\n\n${PUBLIC_URL(businessId)}\n\nMet vriendelijke groet,\n${settings.business_name || 'Het team'}`
                : `Dear customer,\n\nWe invite you to easily book an appointment via our online booking page:\n\n${PUBLIC_URL(businessId)}\n\nKind regards,\n${settings.business_name || 'The team'}`}
            </div>
          </div>

          {/* WhatsApp share */}
          <button
            onClick={() => {
              const msg = nl
                ? `Boek eenvoudig een afspraak via: ${PUBLIC_URL(businessId)}`
                : `Easily book an appointment at: ${PUBLIC_URL(businessId)}`;
              window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
            }}
            style={{ width: '100%', background: '#25D36620', border: '1px solid #25D36655', borderRadius: 14, color: '#25D366', fontSize: 14, fontWeight: 700, padding: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>💬</span>
            {nl ? 'Delen via WhatsApp' : 'Share via WhatsApp'}
          </button>
        </div>
      )}

    </div>
  );
}
